import React, { useEffect, useState } from "react";
import { PublicHeader } from "@/components/layout/AppChrome";
import MarketingFooter from "@/components/layout/MarketingFooter";
import { useI18n } from "@/contexts/I18nContext";
import { api, formatApiError } from "@/lib/api";
import { BUSINESS } from "@/lib/business";
import { CheckCircle2, MessageSquare, Mail, Clock } from "lucide-react";

const CATEGORIES_EN = [
  { id: "billing",       label: "Billing" },
  { id: "payments",      label: "Payments" },
  { id: "subscription",  label: "Subscription" },
  { id: "cancellation",  label: "Cancellation" },
  { id: "refund",        label: "Refund request" },
  { id: "account",       label: "Account access" },
  { id: "technical",     label: "Technical issue" },
  { id: "ai_output",     label: "AI output quality" },
  { id: "other",         label: "Other" },
];
const CATEGORIES_AR = [
  { id: "billing",       label: "الفوترة" },
  { id: "payments",      label: "المدفوعات" },
  { id: "subscription",  label: "الاشتراك" },
  { id: "cancellation",  label: "إلغاء الاشتراك" },
  { id: "refund",        label: "طلب استرداد" },
  { id: "account",       label: "الوصول إلى الحساب" },
  { id: "technical",     label: "مشكلة تقنية" },
  { id: "ai_output",     label: "جودة مخرجات الذكاء الاصطناعي" },
  { id: "other",         label: "أخرى" },
];

const T = {
  ar: {
    eyebrow: "الدعم",
    title: "نحن هنا لمساعدتك",
    intro: "أرسل رسالتك عبر النموذج التالي وسنعود إليك خلال ٤٨ ساعة عمل. للاستفسارات الحسّاسة تفضّل بمراسلة البريد الرسمي أدناه.",
    channels: [
      { icon: Mail, label: `البريد الرسمي: ${BUSINESS.email_support}` },
      { icon: Clock, label: `وقت الرد المتوقع: خلال ${BUSINESS.response_sla_hours} ساعة` },
      { icon: MessageSquare, label: "الردود بالعربية والإنجليزية" },
    ],
    form: {
      name: "الاسم", email: "البريد الإلكتروني", subject: "الموضوع",
      category: "الفئة", message: "رسالتك",
      reference: "رقم اشتراك / رقم عملية (اختياري)",
      submit: "إرسال الطلب", sending: "جارٍ الإرسال…",
      website_label: "لا تملأ هذا الحقل",
    },
    success: {
      title: "تم استلام طلبك بنجاح",
      body: "احتفظ برقم المرجع أدناه للرجوع إليه في أي محادثة مستقبلية معنا.",
      referenceLabel: "الرقم المرجعي",
      new: "إرسال طلب آخر",
    },
    validation: {
      name: "أدخل الاسم (حرفان على الأقل).",
      email: "أدخل بريداً إلكترونياً صالحاً.",
      subject: "الموضوع يجب أن يكون ٣ أحرف على الأقل.",
      message: "الرسالة يجب أن تكون ١٠ أحرف على الأقل.",
      category: "اختر فئة.",
      error: "تعذّر إرسال الطلب.",
      rateLimited: "تم تجاوز عدد المحاولات المسموح. حاول مرة أخرى بعد قليل.",
    },
  },
  en: {
    eyebrow: "Support",
    title: "We're here to help",
    intro: "Send us a message using the form below and we'll get back to you within 48 business hours. For sensitive matters please email our official address below.",
    channels: [
      { icon: Mail, label: `Official email: ${BUSINESS.email_support}` },
      { icon: Clock, label: `Expected response: within ${BUSINESS.response_sla_hours} hours` },
      { icon: MessageSquare, label: "We respond in Arabic and English" },
    ],
    form: {
      name: "Name", email: "Email", subject: "Subject",
      category: "Category", message: "Your message",
      reference: "Subscription / order reference (optional)",
      submit: "Send request", sending: "Sending…",
      website_label: "Leave this field empty",
    },
    success: {
      title: "Your request has been received",
      body: "Keep the reference number below for any future correspondence with us.",
      referenceLabel: "Reference",
      new: "Send another request",
    },
    validation: {
      name: "Enter your name (at least 2 characters).",
      email: "Enter a valid email address.",
      subject: "Subject must be at least 3 characters.",
      message: "Message must be at least 10 characters.",
      category: "Pick a category.",
      error: "Could not send your request.",
      rateLimited: "You've reached the maximum number of attempts. Please try again in a few minutes.",
    },
  },
};

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export default function SupportPage() {
  const { lang } = useI18n();
  const t = T[lang] || T.en;
  const cats = lang === "ar" ? CATEGORIES_AR : CATEGORIES_EN;

  const [form, setForm] = useState({
    name: "", email: "", subject: "",
    category: "billing", message: "", reference: "",
    website: "", // honeypot — must stay empty
  });
  const [errors, setErrors] = useState({});
  const [busy, setBusy] = useState(false);
  const [success, setSuccess] = useState(null);
  const [serverError, setServerError] = useState("");

  useEffect(() => { window.scrollTo({ top: 0 }); }, []);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  function validate() {
    const e = {};
    if (form.name.trim().length < 2) e.name = t.validation.name;
    if (!EMAIL_RE.test(form.email.trim())) e.email = t.validation.email;
    if (form.subject.trim().length < 3) e.subject = t.validation.subject;
    if (form.message.trim().length < 10) e.message = t.validation.message;
    if (!cats.find((c) => c.id === form.category)) e.category = t.validation.category;
    setErrors(e);
    return Object.keys(e).length === 0;
  }

  async function onSubmit(evt) {
    evt.preventDefault();
    setServerError("");
    if (!validate()) return;
    setBusy(true);
    try {
      const { data } = await api.post("/support/tickets", form);
      setSuccess(data?.reference || null);
      setForm((f) => ({ ...f, name: "", email: "", subject: "", message: "", reference: "" }));
    } catch (err) {
      if (err?.response?.status === 429) {
        setServerError(t.validation.rateLimited);
      } else {
        setServerError(formatApiError(err) || t.validation.error);
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <div data-testid="support-page" className="min-h-screen bg-background flex flex-col">
      <PublicHeader />

      <section className="relative overflow-hidden pt-16 md:pt-24 pb-8 md:pb-12 border-b border-border/40">
        <div className="absolute inset-0 pointer-events-none opacity-30"
          style={{ backgroundImage:
              "radial-gradient(1200px 400px at 20% 0%, rgba(184,135,93,0.10), transparent 60%), radial-gradient(1200px 400px at 80% 0%, rgba(0,47,167,0.10), transparent 60%)" }} />
        <div className="relative max-w-4xl mx-auto px-6 md:px-8">
          <div className="text-xs uppercase tracking-[0.25em] text-[#B8875D] mb-3">{t.eyebrow}</div>
          <h1 data-testid="support-title" className="font-display text-4xl sm:text-5xl md:text-6xl leading-[1.05]">{t.title}</h1>
          <p className="mt-4 text-base text-muted-foreground max-w-2xl">{t.intro}</p>
          <div className="mt-6 flex flex-wrap gap-x-6 gap-y-2 text-sm text-muted-foreground">
            {t.channels.map(({ icon: Icon, label }, i) => (
              <span key={i} className="inline-flex items-center gap-2"><Icon className="h-4 w-4 opacity-70" />{label}</span>
            ))}
          </div>
        </div>
      </section>

      <main className="flex-1 py-12 md:py-16">
        <div className="max-w-3xl mx-auto px-6 md:px-8">
          {success ? (
            <div data-testid="support-success" className="rounded-md border border-emerald-500/40 bg-emerald-500/5 p-6 md:p-8">
              <div className="flex items-start gap-3">
                <CheckCircle2 className="h-6 w-6 text-emerald-500 flex-shrink-0 mt-0.5" />
                <div>
                  <h2 className="font-display text-2xl">{t.success.title}</h2>
                  <p className="mt-2 text-muted-foreground">{t.success.body}</p>
                  <div className="mt-4 flex items-baseline gap-3">
                    <span className="text-xs uppercase tracking-widest text-muted-foreground">{t.success.referenceLabel}</span>
                    <span data-testid="support-reference" className="font-mono text-lg tabular-nums">{success}</span>
                  </div>
                  <button
                    data-testid="support-reset-btn"
                    type="button"
                    onClick={() => { setSuccess(null); setServerError(""); }}
                    className="mt-6 inline-flex items-center rounded-sm border border-border px-4 py-2 text-sm hover:bg-muted transition"
                  >
                    {t.success.new}
                  </button>
                </div>
              </div>
            </div>
          ) : (
            <form data-testid="support-form" onSubmit={onSubmit} className="grid gap-5" noValidate>
              <Field label={t.form.name} error={errors.name}>
                <input data-testid="support-name-input" type="text" value={form.name}
                  onChange={(e) => update("name", e.target.value)} maxLength={120}
                  className="w-full px-3 py-2 rounded-sm border border-border bg-background focus:outline-none focus:ring-2 focus:ring-[#B8875D]/40" />
              </Field>
              <Field label={t.form.email} error={errors.email}>
                <input data-testid="support-email-input" type="email" value={form.email}
                  onChange={(e) => update("email", e.target.value)} maxLength={200}
                  className="w-full px-3 py-2 rounded-sm border border-border bg-background focus:outline-none focus:ring-2 focus:ring-[#B8875D]/40" />
              </Field>
              <Field label={t.form.category} error={errors.category}>
                <select data-testid="support-category-select" value={form.category}
                  onChange={(e) => update("category", e.target.value)}
                  className="w-full px-3 py-2 rounded-sm border border-border bg-background focus:outline-none focus:ring-2 focus:ring-[#B8875D]/40">
                  {cats.map((c) => (<option key={c.id} value={c.id}>{c.label}</option>))}
                </select>
              </Field>
              <Field label={t.form.subject} error={errors.subject}>
                <input data-testid="support-subject-input" type="text" value={form.subject}
                  onChange={(e) => update("subject", e.target.value)} maxLength={200}
                  className="w-full px-3 py-2 rounded-sm border border-border bg-background focus:outline-none focus:ring-2 focus:ring-[#B8875D]/40" />
              </Field>
              <Field label={t.form.reference}>
                <input data-testid="support-reference-input" type="text" value={form.reference}
                  onChange={(e) => update("reference", e.target.value)} maxLength={200}
                  className="w-full px-3 py-2 rounded-sm border border-border bg-background focus:outline-none focus:ring-2 focus:ring-[#B8875D]/40" />
              </Field>
              <Field label={t.form.message} error={errors.message}>
                <textarea data-testid="support-message-input" value={form.message}
                  onChange={(e) => update("message", e.target.value)} maxLength={5000} rows={6}
                  className="w-full px-3 py-2 rounded-sm border border-border bg-background focus:outline-none focus:ring-2 focus:ring-[#B8875D]/40" />
                <div className="mt-1 text-xs text-muted-foreground tabular-nums">{form.message.length} / 5000</div>
              </Field>

              {/* Honeypot — visually hidden but not `display:none` (some bots skip that) */}
              <div aria-hidden="true" className="absolute -left-[10000px] top-auto w-px h-px overflow-hidden">
                <label htmlFor="support-website">{t.form.website_label}</label>
                <input id="support-website" name="website" type="text"
                  tabIndex="-1" autoComplete="off" value={form.website}
                  onChange={(e) => update("website", e.target.value)} />
              </div>

              {serverError && (
                <div data-testid="support-error" role="alert" className="rounded-sm border border-red-500/40 bg-red-500/5 text-red-500 text-sm px-3 py-2">
                  {serverError}
                </div>
              )}
              <div>
                <button data-testid="support-submit-btn" type="submit" disabled={busy}
                  className="inline-flex items-center rounded-sm bg-[#002FA7] text-white px-5 py-2.5 text-sm hover:bg-[#002FA7]/90 disabled:opacity-60 transition">
                  {busy ? t.form.sending : t.form.submit}
                </button>
              </div>
            </form>
          )}
        </div>
      </main>

      <MarketingFooter />
    </div>
  );
}

function Field({ label, error, children }) {
  return (
    <label className="block">
      <span className="text-sm mb-1 block text-foreground/90">{label}</span>
      {children}
      {error && <div role="alert" className="mt-1 text-xs text-red-500">{error}</div>}
    </label>
  );
}
